import { z } from "zod";

export const productSchema = z
  .object({
    name: z.string().min(3, "Name must be 3-120 characters").max(120),
    sku: z
      .string()
      .regex(
        /^[A-Z0-9-]{3,32}$/,
        "Use 3-32 uppercase letters, numbers, hyphens",
      ),
    description: z
      .string()
      .min(20, "Description must be 20-2000 characters")
      .max(2000),
    category: z.enum(["Mango", "Box", "Combo", "Accessory"]),
    basePrice: z.number().positive("Base price is required").max(99999),
    compareAtPrice: z.number().optional(),
    stock: z.number().int().min(0).max(100000),
    threshold: z.number().int().min(0),
    images: z.number().int().min(1, "Add 1-8 product images").max(8),
    weightLabel: z.string().min(2, "Weight label required"),
    weightGrams: z.number().int().positive("Weight grams required"),
    weightPrice: z.number().positive("Variant price required"),
    status: z.enum(["Draft", "Published"]),
    availableFrom: z.string().optional(),
    availableTo: z.string().optional(),
    tags: z.string().max(160),
  })
  .refine(
    (data) => !data.compareAtPrice || data.compareAtPrice > data.basePrice,
    {
      message: "Compare price must be higher than base price",
      path: ["compareAtPrice"],
    },
  );

export const customerSchema = z.object({
  name: z.string().min(2, "Full Name is required").max(80),
  mobile: z
    .string()
    .regex(/^[6-9]\d{9}$/, "Enter a valid 10 digit mobile number"),
  email: z.union([z.string().email("Enter a valid email"), z.literal("")]),
  gender: z.enum(["Male", "Female", "Other"]),
  status: z.enum(["Active", "Blocked"]),
});

export const broadcastSchema = z
  .object({
    title: z
      .string()
      .min(4, "Title is required")
      .max(60, "FCM title max is 60 chars"),
    body: z
      .string()
      .min(12, "Body is required")
      .max(240, "FCM body max is 240 chars"),
    imageCount: z.number().int().min(0).max(1).optional(),
    deepLink: z.string().max(255).optional().or(z.literal("")),
    audience: z.enum([
      "All users",
      "Active 30 days",
      "Has placed an order",
      "By city",
    ]),
    scheduleMode: z.enum(["Send now", "Schedule for"]),
    sendAt: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.scheduleMode === "Schedule for" && !data.sendAt) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Date and time is required",
        path: ["sendAt"],
      });
    }
  });

export const settingsSchema = z.object({
  storeName: z.string().min(2, "Store name is required"),
  deliveryCharge: z.number().min(0).max(999),
  freeDeliveryThreshold: z.number().min(0).max(99999),
  deliveryPromise: z.enum(["12 hours", "24 hours", "48 hours"]),
  enquiryPhone: z
    .string()
    .regex(/^\+91[6-9]\d{9}$/, "Enter a valid +91 Indian enquiry phone"),
  supportPhone: z
    .string()
    .regex(/^\+91[6-9]\d{9}$/, "Enter a valid +91 Indian support phone"),
  supportEmail: z.string().email("Enter a valid support email"),
});

export const loginSchema = z.object({
  email: z.string().email("Enter your admin email"),
  password: z.string().min(8, "Password must be at least 8 characters"),
});

export const offerSchema = z.object({
  code: z.string().min(2, "Code is required").max(32).toUpperCase(),
  discountType: z.enum(["percent", "flat"]),
  value: z.number().positive("Value is required"),
  maxDiscount: z.number().min(0).optional(),
  minOrder: z.number().min(0),
  usageLimit: z.number().int().min(1),
  perUserLimit: z.number().int().min(1),
  validFrom: z.string().min(1, "Required"),
  validTo: z.string().min(1, "Required"),
  description: z.string().max(240).optional(),
  status: z.enum(["Active", "Inactive"]),
});
